import React, { useState } from 'react';
import { CronofyOptions } from '../../../../../components/cronofy/CronofyOptions';
import { InterviewStage } from '../../../../../entities/applicant_tracking/InterviewStage';
import ScheduleInterviewPage, { SchedulingSettings } from '../index';
import { User } from '../../../../../entities/User';
import candidateSchedulingImage from '../../../../../images/candidate-experience-mailer-new-survey.png';
import {
  InterviewDatePickerFooter,
  UpsertSelfScheduleInterviewPropTypes,
} from '../InterviewDatePicker/InterviewDatePickerFooter';
import { InterviewCandidateUserService } from '../../../../../services/applicant_tracking/InterviewCandidateUserService';
import { VcsProvider } from '../../../../../enums/IntegrationProvider/VcsProvider';
import {
  appendCurrentRedirectUri,
  appendQueryParam,
} from '../../../../../utils/url';
import { ShareTeamAvailabilityFooter } from '../ShareTeamAvailabilityFooter';
import {
  getSessionSelfSchedulingDto,
  getSessionUUID,
  isDtoValid,
  SESSION_UUID_PARAM,
  setSessionSelfSchedulingDto,
} from '../../../../../utils/applicant_tracking/buildSelfScheduling';
import { appendDateQueryParamsToUrl } from '../utils/buildDateQueryParams';
import { QueryClientProvider } from 'react-query';
import { queryClient } from '../../../../../config/queryClient';
import { FeatureFlagContext } from '../../../../../contexts/FeatureFlagContext';

interface PropTypes {
  cronofyDatePickerOptions: CronofyOptions;
  currentUser: User;
  jobId: number;
  interviewStage: InterviewStage;
  meetingProviders: VcsProvider[];
  defaultMeetingProvider: VcsProvider;
  personalizingSchedulingInvitesV1FfEnabled: boolean;
  personalizingSchedulingInvitesV2FfEnabled: boolean;
}

export default function BulkScheduleInterview(props: PropTypes) {
  const [schedulingSettings, setSchedulingSettings] =
    useState<SchedulingSettings>(null);

  const selfSchedulingDto = getSessionSelfSchedulingDto();

  if (!isDtoValid(selfSchedulingDto)) {
    window.location.href = appendCurrentRedirectUri(
      `/applicant_tracking/interview_candidate_users/schedule/error`,
    );
  }

  const applicationIds = selfSchedulingDto?.applicationIds;
  const totalApplications = selfSchedulingDto?.totalApplications;
  const type = selfSchedulingDto?.type;

  const onConfirm = async (data: UpsertSelfScheduleInterviewPropTypes) => {
    try {
      const dto = await InterviewCandidateUserService.bulkCreateSelfSchedule(
        type,
        props.jobId,
        props.interviewStage.id,
        data.interviewerUserIds,
        data.meetingProvider,
        data.durationMinutes,
        data.dateRange,
        data.scheduleFromHours,
        data.timezone,
        data.meetingOrganizer.id,
        data.calendarEventTemplateId,
        data.calendarEventTemplateDto,
        applicationIds,
      );

      setSessionSelfSchedulingDto(dto);

      let redirectUri =
        '/applicant_tracking/interview_candidate_users/bulk_show';

      redirectUri = appendDateQueryParamsToUrl(
        redirectUri,
        data.scheduleFromHours,
        data.dateRange,
        data.timezone,
      );

      redirectUri = appendQueryParam(
        redirectUri,
        SESSION_UUID_PARAM,
        getSessionUUID(),
      );

      window.location.href = appendCurrentRedirectUri(redirectUri);
    } catch (e) {
      window.location.href = appendCurrentRedirectUri(
        `/applicant_tracking/interview_candidate_users/schedule/error`,
      );
    }
  };

  return (
    <QueryClientProvider client={queryClient}>
      <FeatureFlagContext.Provider
        value={{
          personalizingSchedulingInvitesV1FfEnabled:
            props.personalizingSchedulingInvitesV1FfEnabled,
          personalizingSchedulingInvitesV2FfEnabled:
            props.personalizingSchedulingInvitesV2FfEnabled,
        }}
      >
        <ScheduleInterviewPage
          meetingOrganizer={props.currentUser}
          applicationIds={applicationIds}
          totalApplications={totalApplications}
          title={'Let the candidate schedule the interview'}
          cronofyDatePickerOptions={props.cronofyDatePickerOptions}
          participants={[props.currentUser]}
          interviewStage={props.interviewStage}
          meetingProviders={props.meetingProviders}
          meetingProvider={props.defaultMeetingProvider}
          customImage={candidateSchedulingImage}
          onConfirm={(_data) => null}
          candidateView={false}
          selfScheduleView={true}
          allowChangingMeetingOrganizer={true}
          allowIgnoreParticipantCalendar={false}
          setSchedulingSettings={setSchedulingSettings}
          interviewDatePickerFooter={
            <InterviewDatePickerFooter
              buttonText='Confirm Availability'
              description={
                <ShareTeamAvailabilityFooter
                  schedulingSettings={schedulingSettings}
                />
              }
              onConfirm={onConfirm}
            />
          }
          personalizingSchedulingInvitesV1FfEnabled={
            props.personalizingSchedulingInvitesV1FfEnabled
          }
        />
      </FeatureFlagContext.Provider>
    </QueryClientProvider>
  );
}
